import { z } from 'zod';
import { emailSchema, passwordSchema } from './auth.js';
/**
 * Connexion NATIVE de l'appli mobile (28/09/2026).
 *
 * L'appli ne passe plus par le navigateur pour le mot de passe, la double
 * authentification, Google (comptes du téléphone) et Apple (iPhone) : elle
 * envoie la preuve à l'API, qui rend un CODE D'AUTORISATION ordinaire. Le code
 * s'échange ensuite à /oidc/token avec le `code_verifier`, exactement comme
 * après /oidc/authorize — le reste (refresh, jetons par API, révocation) ne
 * change pas.
 *
 * Seul un client de la suite marqué `connexion_native` (cf. seed : l'appli
 * mobile) y a droit : un site tiers ne doit jamais recevoir le mot de passe
 * d'un compte SEmply.
 */
/** Marqueur, dans `OidcClient.grantTypes`, des clients autorisés à la connexion native. */
export const GRANT_CONNEXION_NATIVE = 'connexion_native';
/** Paramètres OIDC communs : ceux qu'aurait portés /oidc/authorize. */
const parametresOidc = {
    client_id: z.string().min(1).max(100),
    redirect_uri: z.string().url().max(500),
    scope: z.string().min(1).max(500),
    code_challenge: z.string().min(43).max(128),
    code_challenge_method: z.literal('S256'),
    /** Nonce OIDC de l'appli, recopié dans l'id_token du portail. */
    nonce: z.string().min(8).max(512).optional(),
};
/** Ticket de `/demarrer` + id_token rendu par le SDK du fournisseur. */
const preuveFournisseur = {
    id_token: z.string().min(20).max(8192),
    ticket: z.string().min(20).max(4096),
    /**
     * Inscription depuis l'appli (01/10/2026) : présente, elle autorise la
     * CRÉATION du compte quand ce compte Google / Apple n'en a pas encore — les
     * CGU viennent d'être acceptées sur l'écran d'inscription. Absente, un
     * profil inconnu est refusé (`compte_inconnu`), comme avant.
     */
    inscription: z
        .object({
        cgu: z.literal(true, { error: 'CGU à accepter' }),
        /** Code d'invitation, quand les inscriptions se font sur invitation. */
        code_invitation: z.string().trim().max(40).optional(),
    })
        .optional(),
};
/** Code à 6 chiffres reçu par e-mail. */
const codeSixChiffres = z
    .string()
    .trim()
    .regex(/^[0-9]{6}$/, 'Code à 6 chiffres');
export const connexionNativeSchema = z.discriminatedUnion('methode', [
    z.object({
        methode: z.literal('mot_de_passe'),
        email: emailSchema,
        password: z.string().min(1, 'Mot de passe requis').max(1024),
        ...parametresOidc,
    }),
    z.object({
        methode: z.literal('google'),
        ...preuveFournisseur,
        ...parametresOidc,
    }),
    z.object({
        methode: z.literal('apple'),
        ...preuveFournisseur,
        /**
         * Nom transmis par Apple à la PREMIÈRE autorisation seulement, hors
         * id_token (non signé) : il ne sert qu'à pré-remplir, jamais à identifier.
         */
        prenom: z.string().trim().max(100).optional(),
        nom: z.string().trim().max(100).optional(),
        ...parametresOidc,
    }),
    /**
     * Confirmation de l'adresse par le code reçu à l'inscription (01/10/2026),
     * avec le mot de passe choisi : le code prouve la boîte aux lettres, le mot
     * de passe prouve que c'est bien la personne qui s'est inscrite — l'un sans
     * l'autre n'ouvre rien (un compte « pré-créé » par un tiers avec l'adresse
     * d'autrui reste fermé à ce tiers comme au titulaire).
     */
    z.object({
        methode: z.literal('code_email'),
        email: emailSchema,
        password: z.string().min(1, 'Mot de passe requis').max(1024),
        code: codeSixChiffres,
        ...parametresOidc,
    }),
    z.object({
        methode: z.literal('mfa'),
        session_mfa: z.string().min(20).max(4096),
        code: z
            .string()
            .trim()
            .regex(/^[0-9]{6}$/, 'Code à 6 chiffres')
            .optional(),
        code_secours: z.string().trim().min(8).max(32).optional(),
        ...parametresOidc,
    }),
]);
export const demarrerConnexionNativeSchema = z.object({
    client_id: z.string().min(1).max(100),
});
/**
 * Inscription par adresse et mot de passe depuis l'appli (01/10/2026).
 *
 * Mêmes règles que le formulaire du portail (`registerSchema`) — politique de
 * mot de passe, CGU — mais l'adresse se confirme par un CODE à 6 chiffres
 * envoyé par e-mail, saisi dans l'appli : un lien ouvrirait le navigateur. Le
 * compte reste « en attente » jusqu'à la saisie du code (méthode `code_email`
 * de la connexion native), qui ouvre aussitôt la session.
 *
 * `signupCode` et `email` portent ces noms pour l'intercepteur d'ouverture
 * (inscriptions sur invitation), commun aux portes d'inscription.
 */
export const inscriptionNativeSchema = z.object({
    client_id: z.string().min(1).max(100),
    email: emailSchema,
    password: passwordSchema,
    firstName: z.string().trim().min(1).max(100).optional(),
    lastName: z.string().trim().min(1).max(100).optional(),
    acceptTerms: z.literal(true, { error: 'CGU à accepter' }),
    signupCode: z.string().trim().max(40).optional(),
});
/** Nouveau code de confirmation (compte encore « en attente »). */
export const renvoyerCodeNatifSchema = z.object({
    client_id: z.string().min(1).max(100),
    email: emailSchema,
});
//# sourceMappingURL=connexion-native.js.map